// Paths reuse the main catalog's stable IDs. The catalog is supplied by callers
// rather than imported here, keeping curriculum data independent of navigation.
export const learningPaths = [
  {
    id: 'start', label: 'First steps', title: '第一次學投資，從這裡開始',
    description: '先整理能用的錢，再認識報酬、商品與風險，最後寫下自己的計畫。',
    outcome: '能說明自己的資金用途、可投入餘裕，以及下單前需要確認的事。',
    lessonIds: ['cash-flow', 'emergency-fund', 'financial-goals', 'compound-inflation', 'total-return', 'investment-tools', 'risk-drawdown', 'diversification', 'costs-and-fraud', 'investment-plan'],
  },
  {
    id: 'stocks', label: 'Stocks', title: '從報價畫面，開始看懂股票',
    description: '依序辨認持股、報價、K 棒與成交量，再把公司獲利和配息放進判斷。',
    outcome: '能讀出開高低收，分清楚價格紀錄、估值數字與尚未成交的委託。',
    lessonIds: ['stock-ownership', 'stock-quotes', 'candlesticks', 'volume-moving-average', 'financial-statements', 'eps-pe', 'dividends', 'order-liquidity', 'risk-drawdown', 'information-check'],
  },
  {
    id: 'funds', label: 'ETFs', title: '拆開 ETF 與債券的運作方式',
    description: '從持有內容與費用出發，逐步認識折溢價、追蹤、配息、債券與每日槓桿。',
    outcome: '能列出一項基金的投資內容、價格與主要風險，知道哪些數字不能直接比較。',
    lessonIds: ['investment-tools', 'etf-selection', 'etf-nav', 'fund-fees', 'tracking-difference', 'etf-distributions', 'bond-basics', 'bond-yields', 'duration-risk', 'credit-spreads', 'bond-fund-maturity', 'overseas-fx', 'currency-hedging', 'leveraged-etfs'],
  },
  {
    id: 'companies', label: 'Companies', title: '用財報，練習認識一家公司',
    description: '先讀懂報表各自回答的問題，再看獲利、現金、負債與估值，整理需要查證的線索。',
    outcome: '能寫出有數據依據的公司觀察，並區分會計獲利、現金流與股價。',
    lessonIds: ['stock-ownership', 'financial-statements', 'revenue-margins', 'eps-pe', 'roe-leverage', 'book-value', 'free-cash-flow', 'financial-debt', 'valuation-growth', 'cyclical-earnings', 'information-check', 'company-checklist'],
  },
  {
    id: 'practice', label: 'Practice', title: '把理解，變成能執行的習慣',
    description: '從目標與風險開始，比較投入、費用與調整方式，再為提領和情緒變化預留規則。',
    outcome: '能寫下一份可檢查的投資計畫，說明投入、檢視與重新評估的條件。',
    lessonIds: ['financial-goals', 'risk-drawdown', 'asset-allocation', 'diversification', 'dca-mechanics', 'lump-sum-timing', 'fee-compounding', 'rebalancing', 'rebalancing-contributions', 'retirement-planning', 'investor-bias', 'investment-plan'],
  },
  {
    id: 'news', label: 'News', title: '讀財經新聞，先看懂數字在說什麼',
    description: '先查來源，再認識物價、利率、景氣與就業指標，練習對齊期間和發布時間。',
    outcome: '能分辨經濟數字的定義、比較基準與限制，避免把新聞直接當成買賣訊號。',
    lessonIds: ['information-check', 'cpi-inflation', 'real-interest-rates', 'policy-rates', 'yield-curve', 'gdp-growth', 'pmi-cycle', 'labor-market', 'economic-releases'],
  },
  {
    id: 'risk', label: 'Risk', title: '看懂風險數字，再檢查它的盲點',
    description: '從跌幅與波動開始，逐步看懂分散、相關與風險調整績效，再檢查尾端損失與回測資料。',
    outcome: '能說明各項風險數字衡量什麼，列出比較條件與仍未涵蓋的風險。',
    lessonIds: ['risk-drawdown', 'volatility-risk', 'diversification', 'correlation-risk', 'beta-risk', 'sharpe-ratio', 'tail-risk', 'backtest-bias'],
  },
  {
    id: 'behavior', label: 'Mindset', title: '把投資情緒，變成能檢查的決策',
    description: '從常見偏誤出發，辨認虧損、價格錨點、選擇性閱讀與跟風，再建立自己的決策紀錄。',
    outcome: '能區分事實、感受和假設，為每次判斷留下可回顧的理由與檢查條件。',
    lessonIds: ['investor-bias', 'loss-aversion', 'anchoring-bias', 'confirmation-bias', 'herd-fomo', 'overconfidence-trading', 'decision-journal', 'investment-plan'],
  },
  {
    id: 'execution', label: 'Execution', title: '從報價與委託，看懂交易及股數變動',
    description: '先認識報價深度和委託條件，再拆解拆股、增資與回購，核對股數、現金和每股數字。',
    outcome: '能說明價格條件與成交風險，分辨股數變動和真正的財富變化，知道下單前要查哪些規則。',
    lessonIds: ['stock-ownership', 'stock-quotes', 'order-liquidity', 'bid-ask-spread', 'market-limit-orders', 'stop-orders', 'stock-splits', 'rights-issues', 'financial-statements', 'eps-pe', 'share-buybacks', 'information-check'],
  },
  { id: 'research', label: 'Research', title: '拿到基金資料，自己做一次研究',
    description: '從 ETF 入門走到權重、重疊、文件與績效，將資料整理成可核對的問題。',
    outcome: '能讀出基金持有內容及其限制，使用同口徑資料比較，而非只看名稱或配息。',
    lessonIds: ['investment-tools', 'etf-selection', 'index-weighting', 'fund-overlap', 'fund-documents', 'fund-fees', 'tracking-difference', 'benchmark-comparison'] },
  {"id": "taiwan-tax", "label": "Tax", "title": "先把台灣投資所得分類清楚", "description": "從報酬與配息概念開始，再認識114所得年度的股利、海外所得及文件查核。", "outcome": "能辨認申報前需要的資料，區分所得、抵減、計入門檻與最終稅額。", "lessonIds": ["cash-flow", "total-return", "dividends", "overseas-fx", "tw-tax-map", "tw-dividend-tax", "tw-overseas-income", "tw-tax-records"]},
  {"id": "insurance", "label": "Insurance", "title": "從家庭責任，讀懂自己的保障", "description": "先整理目標、資源與可承受風險，再拆開保額、期間、條款與解約。", "outcome": "能列出保障查核問題，區分繳費、保障和現金價值，不只比較商品名稱。", "lessonIds": ["financial-goals", "balance-sheet", "emergency-fund", "risk-drawdown", "insurance-gap", "insurance-periods", "insurance-terms", "insurance-surrender"]},
  {"id": "mortgage", "label": "Mortgage", "title": "把買房和房貸放進現金流", "description": "先看餘裕與預備金，再讀攤還、寬限期、完整費用與交屋準備。", "outcome": "能拆出月付本金與利息，列出買房前要核對的金額、時程和契約條件。", "lessonIds": ["cash-flow", "balance-sheet", "financial-goals", "emergency-fund", "mortgage-amortization", "mortgage-grace-rates", "mortgage-total-cost", "mortgage-cash-buffer"]},
  {"id": "household", "label": "Household", "title": "一起做一份家庭財務安排", "description": "用共同範圍整理收支，替年度支出、負債與收入中斷準備檢查流程。", "outcome": "能避免內部轉帳重複計算，列出專款、付款責任與家庭應變假設。", "lessonIds": ["cash-flow", "balance-sheet", "financial-goals", "emergency-fund", "household-shared-cash", "household-sinking-funds", "household-debt-cost", "household-resilience"]},
];

const hasText = value => typeof value === 'string' && value.trim().length > 0;
const isRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value);

/**
 * Resolve a path against one explicit catalog, never silently dropping a lesson.
 * Completed IDs are browser progress, so duplicates and unknown entries are ignored.
 * Invalid curriculum structure returns null; callers can show an unavailable state.
 * Lesson objects are returned in path order without modifying either input array.
 */
export function getPathProgress(path, lessons, completedIds = []) {
  if (!isRecord(path) || !['id', 'label', 'title', 'description', 'outcome'].every(key => hasText(path[key]))
    || !Array.isArray(path.lessonIds) || path.lessonIds.length === 0
    || !Array.isArray(lessons) || !Array.isArray(completedIds)) return null;
  const wantedIds = new Set();
  for (const id of path.lessonIds) {
    if (!hasText(id) || wantedIds.has(id)) return null;
    wantedIds.add(id);
  }
  const catalog = new Map();
  for (const lesson of lessons) {
    if (!isRecord(lesson) || !hasText(lesson.id) || catalog.has(lesson.id)
      || typeof lesson.minutes !== 'number' || !Number.isFinite(lesson.minutes) || lesson.minutes <= 0) return null;
    catalog.set(lesson.id, lesson);
  }
  const orderedLessons = [];
  const completed = new Set(completedIds.filter(id => catalog.has(id)));
  let completedCount = 0;
  let minutes = 0;
  let remainingMinutes = 0;
  let nextLessonId = null;
  for (const id of path.lessonIds) {
    const lesson = catalog.get(id);
    if (!lesson) return null;
    orderedLessons.push(lesson);
    minutes += lesson.minutes;
    if (completed.has(id)) completedCount += 1;
    else {
      remainingMinutes += lesson.minutes;
      if (nextLessonId === null) nextLessonId = id;
    }
    if (!Number.isFinite(minutes) || !Number.isFinite(remainingMinutes)) return null;
  }
  const total = orderedLessons.length;
  return { lessons: orderedLessons, completedCount, total, minutes, remainingMinutes, nextLessonId, complete: completedCount === total };
}
